import { FormEvent, FunctionComponent, useCallback, useState } from "react";
import { HiPlus, HiX } from "react-icons/hi";
import { EnvData } from "../src/types";
import Button from "./Button";

import FadeIn from "react-fade-in";
import useData from "../src/useData";
import Modal from "./Modal";
import useTenant from "../src/useTenant";
import { Stats } from "../src/types";
import ProductionOnlySettings from "./ProductionOnlySettings";
import { MAX_ENVIRONMENTS } from "../src/constants";
import { useEnvironmentAction, useTenantAction } from "../src/useAction";
import Help from "./Help";

const MAX_ENV_NAME_LENGTH = 16;

export const EnvironmentManagement: FunctionComponent<{}> = () => {
  return (
    <div>
      <h2 className="text-2xl font-space mb-2">Environments</h2>
      <div className="text-sm mb-6">
        Environments are fully separate copies of Oso Cloud. Each environment
        has its own policy, facts, API keys, and access logs.
      </div>
      <ProductionOnlySettings
        youCannot="create new environments in"
        youWillBeAbleTo="create more environments (including a production environment)"
        utmContent="environments"
      >
        <EnvironmentManagementContent />
      </ProductionOnlySettings>
    </div>
  );
};

export const EnvironmentManagementContent: FunctionComponent<{}> = () => {
  const [createEnv, setCreateEnv] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const toggleCreate = () => {
    setCreateEnv(!createEnv);
  };

  const { tenant, refresh } = useTenant();
  const data = tenant?.environments;

  const [envNameString, setEnvNameString] = useState("");
  let envNameError = false;
  if (
    envNameString.length >= MAX_ENV_NAME_LENGTH ||
    envNameString.match(/^[a-z0-9]+$/i) === null ||
    data?.map((env) => env.name === envNameString).some(Boolean)
  ) {
    envNameError = true;
  }

  const atEnvLimit = (data || []).length >= MAX_ENVIRONMENTS;

  const create = useCallback(
    (e: FormEvent) => {
      if (e) e.preventDefault();
      if (!tenant) return;
      setIsCreating(true);
      fetch(`/web/tenants/${tenant.id}/environments`, {
        method: "POST",
        body: `"${envNameString}"`,
      })
        .then(() => {
          refresh().then(
            () => (
              setIsCreating(false), setCreateEnv(false), setEnvNameString("")
            )
          );
        })
        .catch((_e) => {
          setIsCreating(false);
        });
    },
    [tenant, refresh, setIsCreating, setCreateEnv, envNameString]
  );

  const createEnvironmentAction = useTenantAction("create_environment");

  return (
    <div>
      <table className="w-full md:table-fixed">
        <thead>
          <tr className="border-b text-sm text-gray-600 pb-1">
            <td className="">Environment</td>
            <td>Facts</td>
            <td className="flex">
              <span>ID</span>
              <Help>
                The environment ID is encoded in Oso Cloud API keys as the first{" "}
                <code className="text-oso-fact-red">e_[:alphanum:]</code> block
                of characters.
              </Help>
            </td>
            <td></td>
          </tr>
        </thead>
        <tbody>
          {data && data.map((env) => <EnvRecord key={env.pub_id} env={env} />)}
        </tbody>
      </table>
      <div className="mt-4" />
      <Button
        role="button"
        tabIndex={0}
        theme={createEnv ? "default" : "primary"}
        medium
        className="mb-2 focus:outline-2 outline-blue-500"
        onClick={toggleCreate}
        disabled={atEnvLimit || !createEnvironmentAction}
        icon={createEnv ? <HiX /> : <HiPlus />}
      >
        {createEnv ? "Cancel" : "Create"}
      </Button>
      {atEnvLimit && (
        <div>
          Your organization is currently at its environment limit (
          {MAX_ENVIRONMENTS}). Please contact engineering@osohq.com to request
          additional environments.
        </div>
      )}
      {createEnv && (
        <FadeIn transitionDuration={400}>
          <form className="inline-flex items-center h-8" onSubmit={create}>
            <div className="text-base items-center mr-2">
              <input
                className={`border h-full  text-base rounded px-3 py-2 ${
                  envNameError && "outline-red-700 border-red-700"
                }`}
                type="text"
                autoFocus
                value={envNameString}
                onChange={(e) => setEnvNameString(e.target.value)}
                placeholder="Environment Name"
              />
            </div>
            <Button
              role="button"
              tabIndex={0}
              medium
              loading={isCreating}
              disabled={envNameError || isCreating}
              theme="primary"
            >
              Create &rarr;
            </Button>
          </form>

          {envNameError && (
            <div className="text-sm mt-1 text-red-700">
              Environment names must be unique and use fewer than{" "}
              {MAX_ENV_NAME_LENGTH} alphanumeric characters
            </div>
          )}
        </FadeIn>
      )}
    </div>
  );
};

const EnvRecord: FunctionComponent<{
  env: EnvData;
}> = ({ env }) => {
  const isProd = env.name === "production";

  const [confirmDelete, setConfirmDelete] = useState(false);
  const toggleConfirm = () => {
    setConfirmDelete(!confirmDelete);
  };

  const { data: stats } = useData<Stats>(
    `/web/environment/${env.pub_id}/api/stats`
  );

  const deleteEnvironmentAction = useEnvironmentAction(
    "delete_environment",
    env
  );

  return (
    <>
      <tr className="hover:bg-gray-50">
        <td className="py-1">
          <span className="font-normal text-md text-gray-600">
            <b className="text-black">{env.name}</b>
          </span>
        </td>
        <td className="text-sm">
          {stats ? (
            stats.facts.total
          ) : (
            <span className="text-gray-400">&mdash;</span>
          )}
        </td>
        <td>
          <code className="text-sm text-gray-700">{env.pub_id}</code>
        </td>

        <td className="text-right">
          <Button
            role="button"
            theme="delete"
            tabIndex={0}
            xsmall
            icon={<HiX />}
            onClick={toggleConfirm}
            disabled={isProd || !deleteEnvironmentAction}
          >
            Delete
          </Button>
        </td>
      </tr>
      <Modal isOpen={confirmDelete} onClose={toggleConfirm}>
        <ConfirmDeleteView env={env} toggleConfirm={toggleConfirm} />
      </Modal>
    </>
  );
};

const ConfirmDeleteView: FunctionComponent<{
  env: EnvData;
  toggleConfirm: () => void;
}> = ({ env, toggleConfirm }) => {
  const { tenant, refresh } = useTenant();
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDeleteString, setConfirmDeleteString] = useState("");
  let confirmDeleteMatches = false;

  if (confirmDeleteString === env.name) {
    confirmDeleteMatches = true;
  }

  const deleteEnv = useCallback(
    (e: FormEvent) => {
      if (e) e.preventDefault();
      if (!tenant) return;
      setIsDeleting(true);
      fetch(`/web/tenants/${tenant.id}/environments/${env.pub_id}`, {
        method: "DELETE",
      })
        .then(() => {
          refresh().then(
            () => (
              setIsDeleting(false), setConfirmDeleteString(""), toggleConfirm()
            )
          );
        })
        .catch((_e) => {
          setIsDeleting(false);
        });
    },
    [env, refresh, tenant, toggleConfirm]
  );
  return (
    <>
      <div className="text-sm">
        <div>
          Are you sure you want to delete the environment <b>{env.name}</b>?
        </div>
        <div className="mt-2" />
        <div>
          <span className="font-bold text-red-700">
            {" "}
            This action cannot be reversed.
          </span>{" "}
          To continue, type the name of the environment:
        </div>
      </div>
      <div className="mt-2" />
      <form className="items-center" onSubmit={deleteEnv}>
        <div className="text-base items-center h-full">
          <input
            className={`border py-1 px-2 text-base rounded  ${
              !confirmDeleteMatches && "outline-red-700 border-red-700"
            }`}
            type="text"
            value={confirmDeleteString}
            onChange={(e) => setConfirmDeleteString(e.target.value)}
            placeholder={env.name}
          />
        </div>
        <div className="mt-2" />
        <Button
          type="submit"
          tabIndex={0}
          medium
          theme="primary"
          loading={isDeleting}
          disabled={!confirmDeleteMatches || isDeleting}
          className="h-full"
        >
          <span className="items-center inline-flex">
            <span>Confirm</span>
          </span>
        </Button>
      </form>
    </>
  );
};
